import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createBlock as _createBlock, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, isRef as _isRef, normalizeClass as _normalizeClass, vShow as _vShow, renderList as _renderList } from "vue"

const _hoisted_1 = { class: "me-2" }
const _hoisted_2 = {
  key: 2,
  class: "my-3"
}
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { key: 2 }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { key: 1 }
const _hoisted_8 = { key: 2 }
const _hoisted_9 = ["id", "title"]
const _hoisted_10 = { key: 1 }
const _hoisted_11 = { class: "mt-4 mb-2" }
const _hoisted_12 = { class: "mt-5" }
const _hoisted_13 = { key: 0 }
const _hoisted_14 = { key: 0 }
const _hoisted_15 = { class: "mt-5" }

import { ref, computed, onMounted, watch, watchEffect } from 'vue';
import { useRoute, useRouter, RouteLocationRaw } from 'vue-router';
import { provideProjectTaskPolling } from '@/composables/project-task-polling';
import { useQuery } from '@vue/apollo-composable';
import { formatDate, deepParseDates } from '@/utils/date-utils';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { ProjectDocument, ProjectType, UserType } from '@/gql/graphql';
import {
  BAlert,
  BBadge,
  BButton,
  BButtonGroup,
  BCollapse,
  BSpinner,
} from 'bootstrap-vue-next';
import { useUserStore } from '@/store/user';
import ProjectSampleAssociationCreate from '@/components/project/ProjectSampleAssociationCreate.vue';
import ProjectUserAssociationCreate from './project/ProjectUserAssociationCreate.vue';
import ProjectUserAssociationDelete from './project/ProjectUserAssociationDelete.vue';
import ProjectUpdate from '@/components/project/ProjectUpdate.vue';
import SampleList, {
  SampleTypeExtended,
} from '@/components/sample/SampleList.vue';
import { DefaultCode } from '@/components/CodeValidation.vue';
import {
  COMPUTE_TASK_TYPES,
  PAGING_ITEMS_PER_PAGE,
  SHARING_USER_ROLES,
  USER_ROLES,
  TRUNCATE_TEXT_LENGTH,
} from '@/constants';
import SampleReconstructionNextFlow from '@/components/compute/SampleReconstructionNextFlow.vue';
import NextflowTasks from '@/components/compute/NextflowTasks.vue';
import ComputeTasksNested from '@/components/compute/ComputeTasksNested.vue';
import ProjectDelete from '@/components/project/ProjectDelete.vue';
import { truncateText } from '@/utils/string-utils';
import { openRouterLink } from '@/utils/router-utils';

// --- Component References ---

export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectDetail',
  props: {
  id: { type: Number, required: true },
},
  setup(__props) {

const NextflowTasksRef = ref<InstanceType<typeof NextflowTasks>>();
const ComputeTasksNestedRef = ref<InstanceType<typeof ComputeTasksNested>>();
const hasLegacyReconstructions = computed(
  () =>
    !!ComputeTasksNestedRef.value &&
    !ComputeTasksNestedRef.value.tasksQueryInProgress &&
    ComputeTasksNestedRef.value.pagingTotalRows > 0
);

// --- Component properties ---
const props = __props;

// Compute the numeric projectId by converting the route param to a number
const projectId = computed(() => {
  const projectId = Number(props.id);
  if (isNaN(projectId)) {
    throw new Error('Invalid project ID');
  }
  return projectId;
});

const projectTaskPolling = provideProjectTaskPolling(projectId, () =>
  requestRefetchProjectDetails()
);

// --- Go Back Link ---
const backLinkTarget = computed<RouteLocationRaw>(() => {
  const q = route.query;
  if (q.returnTo) {
    const path = decodeURIComponent(String(q.returnTo));
    return { path }; // robust even if it has its own query/hash
  }
  // sensible fallback if user loaded the page directly
  return { name: 'Projects' };
});

const backLinkLabel = computed(() => {
  const q = route.query;
  if (q.returnLabel) {
    return `Back to ${String(q.returnLabel)}`;
  }
  // sensible fallback if user loaded the page directly
  return 'Go Back';
});

// --- Component state ---
const userStore = useUserStore();
const showCompleteDescription = ref(false);
const showFullSamples = ref(false);
const showAdvancedFeatures = ref(false);
const showProjectDeleteModal = ref(false);
const showProjectUpdateModal = ref(false);
const showProjectSampleAssociationCreateModal = ref(false);
const showProjectUserAssociationCreateModal = ref(false);
const showProjectUserAssociationDeleteModal = ref(false);
const showNewSampleReconstructionNextFlowModal = ref(false);
const selectedUser = ref<UserType | null>(null);

// Get the current route and router.
const route = useRoute();
const router = useRouter();

const activeBucketId = ref<number | null>(null);
const activeBucketPathPrefix = ref('');

// Watch the compute tasks (show samples if no compute tasks available)
const computeTasksCount = computed(() => {
  if (NextflowTasksRef.value && ComputeTasksNestedRef.value) {
    return (
      NextflowTasksRef.value.pagingTotalRows +
      ComputeTasksNestedRef.value.pagingTotalRows
    );
  }
  return null;
});

watchEffect(() => {
  const newCount = computeTasksCount.value;
  // collapse sample list if compute tasks not yet fetched, or fetched and not empty
  if (
    NextflowTasksRef.value?.tasksQueryInProgress ||
    ComputeTasksNestedRef.value?.tasksQueryInProgress ||
    newCount === null ||
    newCount > 0
  ) {
    showFullSamples.value = false;
    return;
  }
  // otherwise
  showFullSamples.value = true;
});

// When the currentPathPrefix changes, update the URL's 'path' parameter.
watch(activeBucketPathPrefix, (newPath) => {
  // Update only the 'path' parameter, preserving the rest.
  router.replace({
    query: { ...route.query, path: newPath },
  });

  // Scroll to expanded accordion item
  scrollToBucketTitle(activeBucketId.value);
});

// When the activeAccordion changes (i.e. user selects a different bucket),
// update the URL's 'bucket' parameter and reset the currentPathPrefix.
watch(activeBucketId, (newBucketId, oldBucketId) => {
  // Build new query parameters, always updating the bucket param.
  const newQuery = {
    ...route.query,
    path: activeBucketPathPrefix.value,
    bucket: newBucketId ? newBucketId.toString() : undefined,
  };

  // Only clear the 'path' if there was a previous bucket and it changes.
  if (oldBucketId !== null && newBucketId !== oldBucketId) {
    newQuery.path = '';
    activeBucketPathPrefix.value = '';
  }
  router.replace({ query: newQuery });

  // Scroll to expanded accordion item
  scrollToBucketTitle(newBucketId);
});

// Scroll to expanded accordion item, delay slightly to allow the accordion item to expand
function scrollToBucketTitle(bucketId: number | null) {
  if (bucketId !== null) {
    setTimeout(() => {
      const el = document.getElementById(`bucket-title-${bucketId}`);
      if (el) {
        //el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const elementY = window.scrollY + el.getBoundingClientRect().top;
        const y = elementY - 18;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 500);
  }
}

// When the component mounts, initialize these values from the URL query parameters.
onMounted(() => {
  // If the query parameter 'bucket' is present, use it as the active bucket id.
  if (route.query.bucket) {
    activeBucketId.value = Number(route.query.bucket) || null;
  }
  // If the query parameter 'path' is present, set it as the current folder path.
  if (route.query.path) {
    activeBucketPathPrefix.value = String(route.query.path);
  }
});

// --- User Projects ---
const project = computed<ProjectType | null>(() => {
  const projRes = projectQueryResult.value?.project;
  if (!projRes) {
    return null;
  }
  return deepParseDates(projRes) as ProjectType;
});

// --- Filtered Samples ---
const samples = computed(() => {
  const sampleList: SampleTypeExtended[] = [];
  project.value?.samplesAssociated?.forEach((assocSample) => {
    if (assocSample?.sample) {
      // check if user can edit sample
      const canEditSample = assocSample.sample.bucket?.usersAssociated?.some(
        (assocUser) =>
          assocUser !== null &&
          assocUser.userId === userStore.id &&
          (assocUser.userRole === SHARING_USER_ROLES.OWNER ||
            assocUser.userRole === SHARING_USER_ROLES.EDITOR)
      );

      // push sample to collection
      sampleList.push({
        ...assocSample.sample,
        canEdit: canEditSample || false,
      });
    }
  });
  return sampleList;
});

// --- Default Codes ---
const defaultCodes = computed(() => {
  let codeList: DefaultCode[] = [];
  project.value?.samplesAssociated?.forEach((assocSample) => {
    if (assocSample?.sample?.kit?.code?.code) {
      // insert new code into list, or increment units required
      const codeString = assocSample?.sample?.kit?.code?.code;
      const codeListIndex = codeList.findIndex(
        (code) => code.codeString === codeString
      );
      if (codeListIndex !== -1) {
        codeList[codeListIndex].unitsRequired++;
      } else {
        codeList.push({
          codeString: codeString,
          unitsRequired: 1,
        });
      }
    }
  });
  return codeList;
});

// --- User-Project Role ---
const userRole = computed(() => {
  const userAssoc = project.value?.usersAssociated?.find(
    (assoc) => assoc?.user?.id === userStore.id
  );
  if (userAssoc) {
    return userAssoc.userRole;
  }
  if (userStore.admin) {
    return USER_ROLES.ADMIN;
  }
  return null;
});

const userProjectAssociationError = computed(() => {
  return userRole.value === null && !projectQueryInProgress
    ? 'Access denied, user not associated with the project'
    : null;
});

const canEdit = computed(() => {
  return userRole.value === 'owner' || userRole.value === 'editor';
});
const isDeleted = computed(() => {
  return (
    project.value && project.value.deletedAt && project.value.deletedAt !== null
  );
});

/* GRAPHQL API DETAILS ***********************************************/
const {
  result: projectQueryResult,
  loading: projectQueryInProgress,
  error: projectQueryError,
  refetch: refetchProjectDetails,
} = useQuery(
  ProjectDocument,
  () => ({
    id: projectId.value,
  }),
  {
    enabled: computed(() => Boolean(projectId.value)),
  }
);

/* GRAPHQL API - RE-FETCH DATA WHEN VIEW ACTIVATED ***************************/
function requestRefetchProjectDetails() {
  refetchProjectDetails()
    ?.catch((err) => {
      projectQueryError.value = err;
      console.error('Refetch project details failed:', err);
    })
    ?.then(() => {
      projectQueryInProgress.value = false;
      projectQueryError.value = null;
      console.debug('Refetched project details');
    });
}

// --- Event Handlers ---
function newSampleAssocCreated() {
  showProjectSampleAssociationCreateModal.value = false;
  requestRefetchProjectDetails();
}

function newComputeTaskCreated() {
  void projectTaskPolling.refresh(true);
  showNewSampleReconstructionNextFlowModal.value = false;
  requestRefetchProjectDetails();
}

// --- Advanced Project Settings ---
function closeProjectUserAssociationCreateModal() {
  showProjectUserAssociationCreateModal.value = false;
}

function projectUserAssociationDeleteRequested(user: UserType) {
  selectedUser.value = user;
  showProjectUserAssociationDeleteModal.value = true;
}

function closeProjectUserAssociationDeleteModal() {
  showProjectUserAssociationDeleteModal.value = false;
}

function onProjectDeletedSuccess() {
  console.log('Project deleted successfully, redirecting to projects list');
  showProjectDeleteModal.value = false;
  router.push({ name: 'Projects' });
}

function handleBucketRequest(bucketId: number) {
  activeBucketId.value = bucketId;
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _directive_b_tooltip = _resolveDirective("b-tooltip")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(BButton), {
      onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(openRouterLink)(backLinkTarget.value, _ctx.$router, $event))),
      variant: "outline-dark",
      class: "mb-3",
      size: "sm"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(FontAwesomeIcon), {
          icon: "angle-left",
          class: "me-2"
        }),
        _createTextVNode(" " + _toDisplayString(backLinkLabel.value), 1)
      ]),
      _: 1
    }),
    (project.value?.name)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createElementVNode("h1", null, [
            _createVNode(_unref(FontAwesomeIcon), {
              icon: ['fas', 'magnifying-glass-chart'],
              class: "me-2"
            }),
            _createElementVNode("span", _hoisted_1, _toDisplayString(project.value.name), 1),
            _createVNode(_unref(BButtonGroup), { class: "p-0 link-dark align-baseline btn-xs" }, {
              default: _withCtx(() => [
                (canEdit.value)
                  ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                      key: 0,
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (showProjectUpdateModal.value = true)),
                      id: `btn-edit-project-${project.value.id}`,
                      title: "Edit Project Details",
                      variant: "dark"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'pencil'] })
                      ]),
                      _: 1
                    }, 8, ["id"])), [
                      [
                        _directive_b_tooltip,
                        void 0,
                        void 0,
                        { hover: true }
                      ]
                    ])
                  : _createCommentVNode("", true),
                (userRole.value === _unref(SHARING_USER_ROLES).OWNER)
                  ? (_openBlock(), _createBlock(_unref(BButton), {
                      key: 1,
                      variant: "warning"
                    }, {
                      default: _withCtx(() => [
                        _cache[20] || (_cache[20] = _createElementVNode("i", { class: "fas fa-crown" }, null, -1)),
                        _createTextVNode(" " + _toDisplayString(userRole.value), 1)
                      ]),
                      _: 1
                    }))
                  : (userRole.value === _unref(SHARING_USER_ROLES).EDITOR)
                    ? (_openBlock(), _createBlock(_unref(BButton), {
                        key: 2,
                        variant: "danger"
                      }, {
                        default: _withCtx(() => [
                          _cache[21] || (_cache[21] = _createElementVNode("i", { class: "fas fa-pencil" }, null, -1)),
                          _createTextVNode(" " + _toDisplayString(userRole.value), 1)
                        ]),
                        _: 1
                      }))
                    : (_openBlock(), _createBlock(_unref(BButton), {
                        key: 3,
                        variant: "success"
                      }, {
                        default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                          _createElementVNode("i", { class: "fas fa-eye" }, null, -1),
                          _createTextVNode(" viewer ", -1)
                        ]))]),
                        _: 1
                      })),
                _createVNode(_unref(BButton), { variant: "secondary" }, {
                  default: _withCtx(() => [
                    _cache[23] || (_cache[23] = _createElementVNode("i", { class: "fas fa-calendar" }, null, -1)),
                    _createTextVNode(" " + _toDisplayString(_unref(formatDate)(project.value?.createdAt)), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _cache[24] || (_cache[24] = _createElementVNode("hr", null, null, -1))
        ], 64))
      : _createCommentVNode("", true),
    (isDeleted.value)
      ? (_openBlock(), _createBlock(_unref(BAlert), {
          key: 1,
          show: "",
          modelValue: isDeleted.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isDeleted).value = $event)),
          variant: "warning",
          class: "mt-3"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(FontAwesomeIcon), {
              icon: "exclamation-triangle",
              class: "me-2"
            }),
            _cache[26] || (_cache[26] = _createElementVNode("strong", null, "Project has been deleted", -1)),
            _cache[27] || (_cache[27] = _createElementVNode("br", null, null, -1)),
            _cache[28] || (_cache[28] = _createTextVNode(" Nagivate back to the ", -1)),
            _createVNode(_component_router_link, { to: { name: 'Projects' } }, {
              default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                _createTextVNode("list of your active projects", -1)
              ]))]),
              _: 1
            }),
            _cache[29] || (_cache[29] = _createTextVNode(" or contact support for assistance. ", -1))
          ]),
          _: 1
        }, 8, ["modelValue"]))
      : (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (_unref(projectQueryError))
            ? (_openBlock(), _createBlock(_unref(BAlert), {
                key: 0,
                modelValue: _unref(projectQueryError),
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(projectQueryError) ? (projectQueryError).value = $event : null)),
                variant: "danger",
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(projectQueryError).message) + " ", 1),
                  _createVNode(_unref(BButton), {
                    onClick: requestRefetchProjectDetails,
                    variant: "outline-dark",
                    class: "btn-sm"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'sync'] }),
                      _cache[30] || (_cache[30] = _createTextVNode(" Retry", -1))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue"]))
            : _createCommentVNode("", true),
          _createVNode(_unref(BAlert), {
            modelValue: userProjectAssociationError.value,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((userProjectAssociationError).value = $event)),
            variant: "danger",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(userProjectAssociationError.value) + " ", 1),
              _createVNode(_unref(BButton), {
                onClick: requestRefetchProjectDetails,
                variant: "outline-dark",
                class: "btn-sm"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'sync'] }),
                  _cache[31] || (_cache[31] = _createTextVNode(" Retry", -1))
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["modelValue"]),
          (_unref(projectQueryInProgress) && !project.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(_unref(BSpinner), null, {
                  default: _withCtx(() => [...(_cache[32] || (_cache[32] = [
                    _createTextVNode("Loading project details...", -1)
                  ]))]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true),
          (project.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                (project.value?.description)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_5, [
                      _createTextVNode(_toDisplayString(showCompleteDescription.value
              ? project.value.description
              : _unref(truncateText)(project.value.description, _unref(TRUNCATE_TEXT_LENGTH))) + " ", 1),
                      (
              canEdit.value &&
              (showCompleteDescription.value ||
                project.value?.description.length <= _unref(TRUNCATE_TEXT_LENGTH))
            )
                        ? (_openBlock(), _createBlock(_unref(BButton), {
                            key: 0,
                            onClick: _cache[5] || (_cache[5] = ($event: any) => (showProjectUpdateModal.value = true)),
                            variant: "link",
                            class: "p-0 ms-2 link-dark align-baseline",
                            size: "sm"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'pencil'] })
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true),
                      (project.value.description.length > _unref(TRUNCATE_TEXT_LENGTH))
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            (!showCompleteDescription.value)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_6, " "))
                              : (_openBlock(), _createElementBlock("br", _hoisted_7)),
                            _createVNode(_unref(BButton), {
                              variant: "link",
                              class: _normalizeClass(["p-0 align-baseline link-secondary text-decoration-underline", { 'mt-2': showCompleteDescription.value }]),
                              onClick: _cache[6] || (_cache[6] = ($event: any) => (showCompleteDescription.value = !showCompleteDescription.value)),
                              "aria-expanded": showCompleteDescription.value
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(showCompleteDescription.value ? 'Show Less' : 'Show More'), 1)
                              ]),
                              _: 1
                            }, 8, ["class", "aria-expanded"])
                          ], 64))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true),
                (showProjectUpdateModal.value && canEdit.value)
                  ? (_openBlock(), _createBlock(ProjectUpdate, {
                      key: 1,
                      show: showProjectUpdateModal.value,
                      projectId: props.id,
                      projectName: project.value.name || '',
                      projectDescription: project.value.description || '',
                      onClose: _cache[7] || (_cache[7] = ($event: any) => (showProjectUpdateModal.value = false)),
                      onCloseAndRefresh: _cache[8] || (_cache[8] = ($event: any) => (showProjectUpdateModal.value = false))
                    }, null, 8, ["show", "projectId", "projectName", "projectDescription"]))
                  : _createCommentVNode("", true),
                (samples.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                      _withDirectives((_openBlock(), _createElementBlock("h5", {
                        onClick: _cache[9] || (_cache[9] = ($event: any) => (showFullSamples.value = !showFullSamples.value)),
                        class: "mt-4",
                        style: {"cursor":"pointer"},
                        id: `samples-header-${project.value.id}`,
                        title: 
              showFullSamples.value
                ? 'Click to collapse'
                : 'Click to show sample details'
            
                      }, [
                        (showFullSamples.value)
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                              _createVNode(_unref(FontAwesomeIcon), {
                                icon: ['fas', 'flask'],
                                class: "me-2"
                              }),
                              _cache[33] || (_cache[33] = _createTextVNode(" Samples ", -1))
                            ], 64))
                          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                              (samples.value.length === 0)
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                    _createVNode(_unref(FontAwesomeIcon), {
                                      icon: ['fas', 'flask'],
                                      class: "me-2"
                                    }),
                                    _cache[34] || (_cache[34] = _createTextVNode(" No samples associated with this project ", -1))
                                  ], 64))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                    _createVNode(_unref(BButtonGroup), { size: "sm" }, {
                                      default: _withCtx(() => [
                                        _createVNode(_unref(BButton), {
                                          variant: "dark",
                                          disabled: ""
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'flask'] })
                                          ]),
                                          _: 1
                                        }),
                                        _createVNode(_unref(BButton), { variant: "dark" }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("strong", null, _toDisplayString(samples.value.length) + " sample" + _toDisplayString(samples.value.length === 1 ? '' : 's'), 1)
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _cache[35] || (_cache[35] = _createTextVNode(" associated with this project ", -1))
                                  ], 64))
                            ], 64)),
                        _createVNode(_unref(FontAwesomeIcon), {
                          icon: ['fas', showFullSamples.value ? 'chevron-up' : 'chevron-down'],
                          class: "ms-1"
                        }, null, 8, ["icon"])
                      ], 8, _hoisted_9)), [
                        [
                          _directive_b_tooltip,
                          void 0,
                          void 0,
                          { hover: true }
                        ]
                      ]),
                      _createVNode(_unref(BCollapse), {
                        modelValue: showFullSamples.value,
                        "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((showFullSamples).value = $event))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(SampleList, {
                            samples: samples.value,
                            loading: false,
                            canEdit: canEdit.value,
                            showBucket: true,
                            showKit: true,
                            showAssociatedProjects: false,
                            assocProjectId: props.id,
                            assocProjectName: project.value.name || 'undefined',
                            canEditSampleAssoc: canEdit.value,
                            onRefresh: requestRefetchProjectDetails
                          }, null, 8, ["samples", "canEdit", "assocProjectId", "assocProjectName", "canEditSampleAssoc"]),
                          (canEdit.value)
                            ? (_openBlock(), _createBlock(_unref(BButton), {
                                key: 0,
                                onClick: _cache[10] || (_cache[10] = ($event: any) => (showProjectSampleAssociationCreateModal.value = true)),
                                variant: "outline-dark",
                                size: "sm",
                                class: "me-2"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(FontAwesomeIcon), {
                                    icon: ['fas', 'plus'],
                                    class: "me-2"
                                  }),
                                  _cache[36] || (_cache[36] = _createTextVNode(" Add Sample ", -1))
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true),
                          (showProjectSampleAssociationCreateModal.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                _createVNode(ProjectSampleAssociationCreate, {
                                  show: showProjectSampleAssociationCreateModal.value,
                                  projectId: props.id,
                                  projectName: project.value.name || 'undefined',
                                  onClose: _cache[11] || (_cache[11] = ($event: any) => (showProjectSampleAssociationCreateModal.value = false)),
                                  onCloseAndRefresh: newSampleAssocCreated
                                }, null, 8, ["show", "projectId", "projectName"])
                              ]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      }, 8, ["modelValue"])
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("h5", _hoisted_11, [
                  _createVNode(_unref(FontAwesomeIcon), {
                    icon: ['fas', 'wand-magic-sparkles'],
                    class: "me-2"
                  }),
                  _cache[37] || (_cache[37] = _createTextVNode(" Sample Reconstruction ", -1))
                ]),
                (canEdit.value)
                  ? (_openBlock(), _createBlock(_unref(BButton), {
                      key: 3,
                      onClick: _cache[13] || (_cache[13] = ($event: any) => (showNewSampleReconstructionNextFlowModal.value = true)),
                      variant: "outline-dark",
                      size: "sm",
                      class: "my-3"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(FontAwesomeIcon), {
                          icon: ['fas', 'plus'],
                          class: "me-2"
                        }),
                        _cache[39] || (_cache[39] = _createTextVNode(" Sample Reconstruction ", -1)),
                        _createVNode(_unref(BBadge), { variant: "warning" }, {
                          default: _withCtx(() => [...(_cache[38] || (_cache[38] = [
                            _createElementVNode("i", { class: "fab fa-neos" }, null, -1),
                            _createTextVNode(" Nextflow ", -1)
                          ]))]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (showNewSampleReconstructionNextFlowModal.value)
                  ? (_openBlock(), _createBlock(SampleReconstructionNextFlow, {
                      key: 4,
                      show: showNewSampleReconstructionNextFlowModal.value,
                      projectId: projectId.value,
                      projectName: project.value?.name || 'undefined',
                      unitsRequired: project.value?.samplesAssociated?.length || 0,
                      defaultCodes: defaultCodes.value,
                      defaultName: `Sample Reconstruction ${(computeTasksCount.value || 0) + 1}`,
                      samples: samples.value,
                      onClose: _cache[14] || (_cache[14] = ($event: any) => (showNewSampleReconstructionNextFlowModal.value = false)),
                      onCloseAndRefresh: newComputeTaskCreated
                    }, null, 8, ["show", "projectId", "projectName", "unitsRequired", "defaultCodes", "defaultName", "samples"]))
                  : _createCommentVNode("", true),
                _cache[54] || (_cache[54] = _createElementVNode("h5", { class: "mt-4 mb-3" }, "Nextflow reconstructions", -1)),
                _createVNode(NextflowTasks, {
                  id: "compute-tasks-nextflow",
                  ref_key: "NextflowTasksRef",
                  ref: NextflowTasksRef,
                  "project-id": projectId.value,
                  "paging-items-per-page": _unref(PAGING_ITEMS_PER_PAGE),
                  "prevent-redirect-to-bucket": true,
                  "message-no-items-found": "No Nextflow reconstruction tasks have been executed for this project.",
                  "task-name-column-title": "Name",
                  onBucketRequest: handleBucketRequest
                }, null, 8, ["project-id", "paging-items-per-page"]),
                _withDirectives(_createElementVNode("div", null, [
                  _cache[40] || (_cache[40] = _createElementVNode("h5", { class: "mt-4 mb-3" }, "Legacy reconstructions", -1)),
                  _createVNode(ComputeTasksNested, {
                    id: "compute-tasks-nested",
                    ref_key: "ComputeTasksNestedRef",
                    ref: ComputeTasksNestedRef,
                    "task-types": [
              _unref(COMPUTE_TASK_TYPES).COMPUTE_PIPELINE,
              _unref(COMPUTE_TASK_TYPES).DATA_PIPELINE,
            ],
                    "exclude-subtask-types": [
              _unref(COMPUTE_TASK_TYPES).NEXTFLOW_PIPELINE,
              _unref(COMPUTE_TASK_TYPES).FTP_TRANSFER,
              _unref(COMPUTE_TASK_TYPES).BASESPACE_TRANSFER,
              _unref(COMPUTE_TASK_TYPES).WASABI_TRANSFER,
              _unref(COMPUTE_TASK_TYPES).BCL_TO_FASTQ,
            ],
                    "project-id": projectId.value,
                    "paging-items-per-page": _unref(PAGING_ITEMS_PER_PAGE),
                    "prevent-redirect-to-bucket": true,
                    "message-no-items-found": "No legacy reconstruction tasks have been executed for this project.",
                    "task-name-column-title": "Name",
                    onBucketRequest: handleBucketRequest
                  }, null, 8, ["task-types", "exclude-subtask-types", "project-id", "paging-items-per-page"])
                ], 512), [
                  [_vShow, hasLegacyReconstructions.value]
                ]),
                (canEdit.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
                      _createVNode(_unref(BButtonGroup), {
                        class: "mt-4",
                        size: "sm"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(BButton), {
                            onClick: _cache[15] || (_cache[15] = ($event: any) => (showAdvancedFeatures.value = !showAdvancedFeatures.value)),
                            variant: "outline-dark"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(FontAwesomeIcon), {
                                icon: "cogs",
                                class: "me-1"
                              }),
                              _cache[41] || (_cache[41] = _createTextVNode(" Advanced ", -1)),
                              _createVNode(_unref(FontAwesomeIcon), {
                                icon: showAdvancedFeatures.value ? 'angle-down' : 'angle-right'
                              }, null, 8, ["icon"])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }),
                      _createVNode(_unref(BCollapse), {
                        modelValue: showAdvancedFeatures.value,
                        "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((showAdvancedFeatures).value = $event))
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("h5", _hoisted_12, [
                            _createVNode(_unref(FontAwesomeIcon), {
                              icon: "user-shield",
                              class: "me-1"
                            }),
                            _cache[42] || (_cache[42] = _createTextVNode(" Users & Sharing ", -1))
                          ]),
                          (project.value?.usersAssociated?.length === 0)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_13, " No users associated with this project. "))
                            : _createCommentVNode("", true),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(project.value?.usersAssociated, (assoc, idx) => {
                            return (_openBlock(), _createElementBlock(_Fragment, { key: idx }, [
                              (assoc && assoc.user)
                                ? (_openBlock(), _createElementBlock("p", _hoisted_14, [
                                    _cache[44] || (_cache[44] = _createElementVNode("strong", null, "Role:", -1)),
                                    _createTextVNode(" " + _toDisplayString(assoc.userRole), 1),
                                    _cache[45] || (_cache[45] = _createElementVNode("br", null, null, -1)),
                                    _cache[46] || (_cache[46] = _createElementVNode("strong", null, "Name:", -1)),
                                    _createTextVNode(" " + _toDisplayString(assoc.user?.givenName) + " " + _toDisplayString(assoc.user?.familyName) + " ", 1),
                                    _cache[47] || (_cache[47] = _createElementVNode("br", null, null, -1)),
                                    _cache[48] || (_cache[48] = _createElementVNode("strong", null, "Email:", -1)),
                                    _createTextVNode(" " + _toDisplayString(assoc.user?.email), 1),
                                    _cache[49] || (_cache[49] = _createElementVNode("br", null, null, -1)),
                                    (canEdit.value && assoc.userRole !== 'owner')
                                      ? (_openBlock(), _createBlock(_unref(BButton), {
                                          key: 0,
                                          onClick: ($event: any) => (projectUserAssociationDeleteRequested(assoc.user)),
                                          variant: "outline-danger",
                                          size: "sm"
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_unref(FontAwesomeIcon), {
                                              icon: ['fas', 'times'],
                                              class: "me-2"
                                            }),
                                            _cache[43] || (_cache[43] = _createTextVNode(" Remove ", -1))
                                          ]),
                                          _: 1
                                        }, 8, ["onClick"]))
                                      : _createCommentVNode("", true)
                                  ]))
                                : _createCommentVNode("", true)
                            ], 64))
                          }), 128)),
                          (canEdit.value)
                            ? (_openBlock(), _createBlock(_unref(BButton), {
                                key: 1,
                                onClick: _cache[16] || (_cache[16] = ($event: any) => (showProjectUserAssociationCreateModal.value = true)),
                                variant: "outline-dark",
                                size: "sm"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(FontAwesomeIcon), {
                                    icon: "plus",
                                    class: "me-1"
                                  }),
                                  _cache[50] || (_cache[50] = _createTextVNode(" Share project ", -1))
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true),
                          (showProjectUserAssociationCreateModal.value)
                            ? (_openBlock(), _createBlock(ProjectUserAssociationCreate, {
                                key: 2,
                                show: showProjectUserAssociationCreateModal.value,
                                projectId: props.id,
                                projectName: project.value.name || '',
                                onClose: closeProjectUserAssociationCreateModal,
                                onCloseAndRefresh: closeProjectUserAssociationCreateModal
                              }, null, 8, ["show", "projectId", "projectName"]))
                            : _createCommentVNode("", true),
                          (showProjectUserAssociationDeleteModal.value && selectedUser.value)
                            ? (_openBlock(), _createBlock(ProjectUserAssociationDelete, {
                                key: 3,
                                show: showProjectUserAssociationDeleteModal.value,
                                projectId: props.id,
                                user: selectedUser.value,
                                projectName: project.value.name || '',
                                onClose: closeProjectUserAssociationDeleteModal,
                                onCloseAndRefresh: closeProjectUserAssociationDeleteModal
                              }, null, 8, ["show", "projectId", "user", "projectName"]))
                            : _createCommentVNode("", true),
                          _createElementVNode("h5", _hoisted_15, [
                            _createVNode(_unref(FontAwesomeIcon), {
                              icon: "trash",
                              class: "me-1"
                            }),
                            _cache[51] || (_cache[51] = _createTextVNode(" Delete Project ", -1))
                          ]),
                          _cache[53] || (_cache[53] = _createElementVNode("p", {
                            variant: "warning",
                            show: ""
                          }, [
                            _createElementVNode("strong", null, "Warning:"),
                            _createTextVNode(" This action is irreversible. The samples and their input data will remain in the system, but all associated data produced in the project will be removed. Please make sure to download any data you wish to keep before proceeding. ")
                          ], -1)),
                          _createVNode(_unref(BButton), {
                            onClick: _cache[17] || (_cache[17] = ($event: any) => (showProjectDeleteModal.value = true)),
                            variant: "danger",
                            size: "sm"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(FontAwesomeIcon), {
                                icon: "trash",
                                class: "me-1"
                              }),
                              _cache[52] || (_cache[52] = _createTextVNode(" Delete Project ", -1))
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["modelValue"])
                    ], 64))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ])),
    (showProjectDeleteModal.value && project.value && project.value.id)
      ? (_openBlock(), _createBlock(ProjectDelete, {
          key: 3,
          show: showProjectDeleteModal.value,
          projectId: project.value.id,
          projectName: project.value.name || '',
          onClose: _cache[19] || (_cache[19] = ($event: any) => (showProjectDeleteModal.value = false)),
          onCloseAndRefresh: onProjectDeletedSuccess
        }, null, 8, ["show", "projectId", "projectName"]))
      : _createCommentVNode("", true)
  ]))
}
}

})